<template>
  <div class="form-container">
    <div class="form-header">
      <h2>{{ isEditing ? 'Edit Organization' : 'Create New Organization' }}</h2>
    </div>
    <form @submit.prevent="handleSubmit" class="form">
      <div class="form-section">
        <h3>Information</h3>
        <div class="form-group two-thirds-width">
          <label for="logo_url">Logo</label>
          <ImageUploadField
            v-model="formData.logo_url"
            folder="distributor_content"
            max-height="100px"
          />
        </div>

        <div class="form-group half-width">
          <label for="name" class="required">Full Legal Name</label>
          <input
            :disabled="isEditing && !isSuperuser()"
            type="text"
            id="name"
            v-model.trim="formData.name"
            class="form-control"
            placeholder="Legal name of the company or organization"
            maxlength="80"
            required
          >
        </div>

        <div class="form-group half-width">
          <label for="registration">VAT/Registration Number</label>
          <input
            type="text"
            id="registration"
            v-model.trim="formData.registration"
            class="form-control"
            maxlength="80"
          >
        </div>

        <div class="form-group half-width">
          <label for="website_url" :class="{ required: !isSuperuser() }">Website URL</label>
          <input
            type="url"
            id="website_url"
            v-model.trim="formData.website_url"
            class="form-control"
            maxlength="255"
            :required="!isSuperuser()"
          >
        </div>

        <div class="form-group half-width">
          <label for="phone" :class="{ required: !isSuperuser() }">Phone</label>
          <input
            type="tel"
            id="phone"
            v-model.trim="formData.phone"
            class="form-control"
            maxlength="20"
            :required="!isSuperuser()"
          >
        </div>

        <div class="form-group half-width" v-if="!isEditing && !isSuperuser()">
          <label for="role" class="required">Your Role / Title</label>
          <input
            type="text"
            id="role"
            v-model.trim="formData.role"
            class="form-control"
            placeholder="e.g. Executive Producer"
            maxlength="80"
            required
          />
        </div>

        <div class="form-group full-width">
          <label for="address_line_1" :class="{ required: !isSuperuser() }">Address</label>
          <input
            type="text"
            id="address_line_1"
            v-model.trim="formData.address_line_1"
            class="form-control"
            placeholder="Address Line 1"
            maxlength="255"
            :required="!isSuperuser()"
          >
        </div>

        <div class="form-group full-width">
          <input
            type="text"
            id="address_line_2"
            v-model.trim="formData.address_line_2"
            class="form-control"
            placeholder="Address Line 2"
            maxlength="255"
          >
        </div>

        <div class="form-group half-width">
          <label for="city" :class="{ required: !isSuperuser() }">City</label>
          <input
            type="text"
            id="city"
            v-model.trim="formData.city"
            class="form-control"
            maxlength="80"
            :required="!isSuperuser()"
          >
        </div>

        <div class="form-group quarter-width">
          <label for="state">State</label>
          <input
            type="text"
            id="state"
            v-model.trim="formData.state"
            class="form-control"
            maxlength="80"
          >
        </div>

        <div class="form-group quarter-width">
          <label for="postal_code">Postal Code / ZIP</label>
          <input
            type="text"
            id="postal_code"
            v-model.trim="formData.postal_code"
            class="form-control"
            maxlength="20"
          >
        </div>

        <div class="form-group half-width">
          <label for="country" :class="{ required: !isSuperuser() }">Country</label>
          <input
            type="text"
            id="country"
            v-model.trim="formData.country"
            class="form-control"
            maxlength="80"
            :required="!isSuperuser()"
          >
        </div>
      </div>

      <OrganizationMembers 
        v-if="isEditing && isSuperuser()"
        :organization-id="organizationId"
      />

      <div class="form-actions">
        <button type="submit" class="submit-button big">
          {{ isEditing ? 'Save Changes' : 'Create Organization' }}
        </button>
        <button type="button" class="cancel-button medium" @click="handleCancel">
          Cancel
        </button>
      </div>
    </form>

  </div>
</template>

<script setup>
import axios from 'axios';
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { handleApiError } from '@/scripts/shared-utils';
import { updateUserInfo } from '@/scripts/auth';
import { isSuperuser } from '@/scripts/permissions';
import ImageUploadField from '@/components/ImageUploadField.vue';
import OrganizationMembers from '@/components/OrganizationMembers.vue';

const route = useRoute();
const router = useRouter();
const isEditing = computed(() => route.params.organization_id ? true : false);
const organizationId = computed(() => route.params.organization_id);

const formData = ref({
  name: '',
  logo_url: '',
  website_url: '',
  company: '',
  registration: '',
  phone: '',
  address_line_1: '',
  address_line_2: '',
  city: '',
  state: '',
  country: '',
  postal_code: '',
  role: '',
});

const handleSubmit = async () => {
  try {
    if (isEditing.value) {
      const editableFields = [
        'name',
        'logo_url',
        'website_url',
        'registration',
        'phone',
        'address_line_1',
        'address_line_2',
        'city',
        'state',
        'country',
        'postal_code'
      ];
      
      const payload = {};
      editableFields.forEach(field => {
        if (field in formData.value) {
          payload[field] = formData.value[field];
        }
      });
      
      await axios.put(`/api/organizations/${organizationId.value}`, payload);
    } else {
      await axios.post('/api/organizations', formData.value);
      await updateUserInfo();
    }
    closeForm();
  } catch (error) {
    handleApiError(error, 'submitting organization');
    alert('Error saving organization. Please try again.');
  }
};

const handleCancel = () => {
  router.back();
};

const closeForm = () => {
  router.push(isSuperuser() ? '/admin/organizations' : '/organization');
};

onMounted(async () => {
  if (isEditing.value) {
    try {
      const response = await axios.get(`/api/organizations/${organizationId.value}`);
      formData.value = {
        name: response.data.name,
        logo_url: response.data.logo_url || '',
        website_url: response.data.website_url || '',
        company: response.data.company || '',
        registration: response.data.registration || '',
        phone: response.data.phone || '',
        address_line_1: response.data.address_line_1 || '',
        address_line_2: response.data.address_line_2 || '',
        city: response.data.city || '',
        state: response.data.state || '',
        country: response.data.country || '',
        postal_code: response.data.postal_code || '',
      };
    } catch (error) {
      handleApiError(error, 'fetching organization');
    }
  }
});
</script>

<style scoped>
@import '@/styles/shared-form.css';
</style>
